import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Close,
  Visibility,
  VisibilityOff,
  Login,
  PersonAdd
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AuthModal = ({ open, onClose }) => {
  const [tab, setTab] = useState(0); // 0 = Connexion, 1 = Inscription
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    profession: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const professions = [
    'Plombier', 'Électricien', 'Maçon', 'Coiffeur', 'Garagiste',
    'Jardinier', 'Peintre', 'Menuisier', 'Fleuriste', 'Carreleur',
    'Esthéticien', 'Masseur', 'Service de ménage', 'Carrossier',
    'Contrôle technique', 'Ramoneur', 'Couvreur'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // 🎯 FONCTION DE REDIRECTION INTELLIGENTE
  const getRedirectPath = (profession, userType = 'artisan') => {
    // Routes spécialisées selon le métier
    const professionRoutes = {
      'Électricien': '/dashboard/electricien',
      'Plombier': '/dashboard/plombier', 
      'Maçon': '/dashboard/macon',
      'Coiffeur': '/dashboard/coiffeur',
      'Garagiste': '/dashboard/garagiste',
      'Jardinier': '/dashboard/jardinier',
      'Peintre': '/dashboard/peintre',
      'Menuisier': '/dashboard/menuisier',
      'Fleuriste': '/dashboard/fleuriste',
      'Carreleur': '/dashboard/carreleur',
      'Esthéticien': '/dashboard/estheticien',
      'Masseur': '/dashboard/masseur',
      'Service de ménage': '/dashboard/menage',
      'Carrossier': '/dashboard/carrossier',
      'Contrôle technique': '/dashboard/controle-technique',
      'Ramoneur': '/dashboard/ramoneur',
      'Couvreur': '/dashboard/couvreur'
    };

    // Si admin, redirection vers dashboard admin
    if (userType === 'admin' || formData.email === 'admin@lys-artisans.fr') {
      return '/dashboard-admin';
    }

    // Redirection spécialisée selon métier
    return professionRoutes[profession] || '/dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔐 Tentative de connexion/inscription:', { tab, email: formData.email });

    try {
      if (tab === 0) {
        // CONNEXION
        if (!formData.email || !formData.password) {
          setError('Veuillez remplir tous les champs');
          setLoading(false);
          return;
        }

        // Détection du type d'utilisateur
        const isAdmin = formData.email === 'admin@lys-artisans.fr' || formData.email.includes('admin');
        const userType = isAdmin ? 'admin' : 'artisan';

        // Simulation de connexion réussie avec données enrichies
        const userData = {
          id: 'user-' + Date.now(),
          email: formData.email,
          name: isAdmin ? 'Administrateur LYS' : 'Utilisateur Demo',
          profession: isAdmin ? 'Administrateur' : 'Artisan',
          userType: userType,
          businessName: isAdmin ? 'LYS Artisans Admin' : 'Mon Entreprise',
          businessType: isAdmin ? 'admin' : 'artisan',
          firstName: isAdmin ? 'Admin' : 'Utilisateur',
          lastName: isAdmin ? 'LYS' : 'Demo',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString()
        };

        // Stocker en mode DEMO compatible avec AuthContext
        localStorage.setItem('token', 'DEMO');
        localStorage.setItem('userToken', 'demo-token-' + Date.now());
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('userType', userType);

        const redirectPath = getRedirectPath(userData.profession, userType);
        
        setSuccess(`✅ Connexion réussie ! Redirection vers votre ${isAdmin ? 'panel admin' : 'dashboard'}...`);
        setTimeout(() => {
          navigate(redirectPath);
          onClose();
        }, 1500);

      } else {
        // INSCRIPTION
        if (!formData.email || !formData.password || !formData.firstName || !formData.profession) {
          setError('Veuillez remplir tous les champs obligatoires');
          setLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setError('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }

        // Simulation d'inscription réussie avec données complètes
        const userData = {
          id: 'user-' + Date.now(),
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName || ''}`.trim(),
          profession: formData.profession,
          userType: 'artisan',
          businessName: `${formData.profession} ${formData.firstName}`,
          businessType: formData.profession.toLowerCase(),
          firstName: formData.firstName,
          lastName: formData.lastName || '',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          isNewUser: true
        };

  // Stocker en mode DEMO compatible avec AuthContext
  localStorage.setItem('token', 'DEMO');
  localStorage.setItem('userToken', 'demo-token-' + Date.now());
  localStorage.setItem('userData', JSON.stringify(userData));
  localStorage.setItem('userType', 'artisan');

        const redirectPath = getRedirectPath(formData.profession, 'artisan');

        setSuccess(`🎉 Inscription réussie ! Redirection vers votre dashboard ${formData.profession}...`);
        setTimeout(() => {
          navigate(redirectPath);
          onClose();
        }, 1500);
      }

    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      profession: ''
    });
    setError('');
    setSuccess('');
    setTab(0);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="div">
            {tab === 0 ? '🔑 Connexion' : '📝 Inscription'}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs 
          value={tab} 
          onChange={(e, newValue) => setTab(newValue)}
          variant="fullWidth"
          sx={{ mb: 3 }}
        >
          <Tab label="Se connecter" icon={<Login />} />
          <Tab label="S'inscrire" icon={<PersonAdd />} />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {tab === 1 && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Prénom *"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <TextField
                  fullWidth
                  label="Nom"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </Box>

              <TextField
                fullWidth
                select
                label="Profession *"
                name="profession"
                value={formData.profession}
                onChange={handleChange}
                SelectProps={{ native: true }}
                sx={{ mb: 2 }}
                required
              >
                <option value="">Sélectionnez votre métier</option>
                {professions.map((prof) => (
                  <option key={prof} value={prof}>{prof}</option>
                ))}
              </TextField>
            </>
          )}

          <TextField
            fullWidth
            label="Email *"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label="Mot de passe *"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            sx={{ mb: 2 }}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {tab === 1 && (
            <TextField
              fullWidth
              label="Confirmer le mot de passe *"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              sx={{ mb: 2 }}
              required
            />
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ 
              mt: 2, 
              py: 1.5,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
            }}
          >
            {loading ? '⏳ ' : (tab === 0 ? '🔑 ' : '📝 ')}
            {loading ? 'Traitement...' : (tab === 0 ? 'Se connecter' : "S'inscrire")}
          </Button>

          {tab === 0 && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pas encore de compte ?{' '}
                <Button 
                  variant="text" 
                  onClick={() => setTab(1)}
                  sx={{ textDecoration: 'underline' }}
                >
                  Créer un compte
                </Button>
              </Typography>

              <Typography variant="body2" sx={{ mt: 1, mb: 1, fontWeight: 'bold', color: '#667eea' }}>
                🎭 Comptes de démonstration :
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      email: 'moretizidonna@gmail.com',
                      password: 'demo123'
                    });
                  }}
                  sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                >
                  ⚡ Électricien
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      email: 'plombier@demo.com',
                      password: 'demo123'
                    });
                  }}
                  sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                >
                  🔧 Plombier
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      email: 'coiffeur@demo.com',
                      password: 'demo123'
                    });
                  }}
                  sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                >
                  💇 Coiffeur
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;